/**
 * PATH: packages/job-hub/src/jobs/qa/qaTemplatesApi.ts
 * FILE_ID: QA-TEMPLATES-API-001
 * PURPOSE: List, load, insert templates and search products on the QA client.
 * LANDMINE: Reuse the QA transport, never qbApi() from quote-builder.
 */
import type { JobHubHttpClient } from '../jobHubDataAdapter'
import { normalizeList } from './qaApiHelpers'
import { postQaInBackground } from './qaBackgroundWrite'
import type {
  QaProductHit,
  QaFromTemplatesPayload,
  QaBlankInsertBody,
  QaBlankInsertResult,
  QaTemplateCreateBody,
  QaTemplateDetail,
  QaTemplateInsertPayload,
  QaTemplateInsertResult,
  QaTemplateSummary,
} from './qaTemplateTypes'
import type { QaInspectionSummary } from './qaTypes'

export function createQaTemplatesApi(
  client: JobHubHttpClient,
  headers: Record<string, string> | undefined,
) {
  return {
    async listTemplates(jobId?: number): Promise<QaTemplateSummary[]> {
      const base = { status: 'published' }
      const response = await client.get('/api/qa/templates/', {
        params: { ...base, ...(jobId != null ? { job_id: jobId } : {}) },
        headers,
      })
      const rows = normalizeList<QaTemplateSummary>(response.data)
      const jobKeyPresent = rows.every((row) => Object.prototype.hasOwnProperty.call(row, 'job'))
      if (jobId == null || jobKeyPresent) return rows
      const org = await client.get('/api/qa/templates/', { params: base, headers })
      const orgIds = new Set(normalizeList<QaTemplateSummary>(org.data).map((row) => row.id))
      return rows.map((row) => (orgIds.has(row.id) ? row : { ...row, job: jobId }))
    },
    async restoreDefaults(jobId: number): Promise<{ archived: number }> {
      const response = await client.post(
        '/api/qa/templates/restore-defaults/',
        { job_id: jobId },
        { headers },
      )
      const archived = Number((response.data as { archived?: unknown })?.archived)
      return { archived: Number.isFinite(archived) ? archived : 0 }
    },
    async generateFromTemplates(payload: QaFromTemplatesPayload): Promise<QaInspectionSummary> {
      const response = await postQaInBackground<QaInspectionSummary>(
        client, '/api/qa/inspections/from-templates/', payload, headers,
      )
      return response.data
    },
    async getTemplate(id: string): Promise<QaTemplateDetail> {
      const response = await client.get(`/api/qa/templates/${id}/`, { headers })
      return response.data as QaTemplateDetail
    },
    async insertTemplate(id: string, payload: QaTemplateInsertPayload): Promise<QaTemplateInsertResult> {
      const response = await postQaInBackground<QaTemplateInsertResult>(
        client, `/api/qa/templates/${id}/insert/`, payload, headers,
      )
      return response.data
    },
    async insertBlank(body: QaBlankInsertBody): Promise<QaBlankInsertResult> {
      const response = await postQaInBackground<QaBlankInsertResult>(
        client, '/api/qa/inspections/blank/', body, headers,
      )
      return response.data
    },
    async createTemplate(body: QaTemplateCreateBody): Promise<{ id: string }> {
      const response = await client.post('/api/qa/templates/', body, { headers })
      return response.data as { id: string }
    },
    async publishTemplate(id: string): Promise<void> {
      await client.post(`/api/qa/templates/${id}/publish/`, {}, { headers })
    },
    async searchProducts(q: string, limit = 20): Promise<QaProductHit[]> {
      const orgId = headers?.['X-Organisation-Id']
      const response = await client.get('/api/quotebuilder/products/search/', {
        params: { q, limit, ...(orgId ? { org_id: orgId } : {}) },
        headers,
      })
      return normalizeList<QaProductHit>(response.data)
    },
  }
}
